import { Spin } from 'antd';
import {
  LegalDocumentAudience,
  LegalDocumentType,
} from '@/api/legalDocuments';
import { usePublicLegalDocument } from '@/hooks/useLegalDocuments';
import { sanitizeHtml } from '@/helpers/sanitizeHtml';
import './legal-document.scss';

interface Props {
  documentType: LegalDocumentType;
  audience: LegalDocumentAudience;
}

const formatEffectiveDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

/**
 * TCX-668: fetches and renders the CURRENT published version of a legal-document
 * channel from the public endpoint, so the Privacy / Terms pages always reflect
 * what the admin most recently published. Content is sanitized server-side at
 * publish time and again on the client via sanitizeHtml before it is injected
 * with dangerouslySetInnerHTML (TCX-788 defense-in-depth; same pattern as the
 * admin PreviewModal).
 */
export const RenderedLegalDocument = ({ documentType, audience }: Props) => {
  const { data, isLoading, isError } = usePublicLegalDocument(
    documentType,
    audience,
  );

  if (isLoading) {
    return (
      <div className="legal-document-loading">
        <Spin />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="legal-document-error">
        This document is currently unavailable. Please try again later.
      </div>
    );
  }

  const effective = formatEffectiveDate(data.publishedAt);

  return (
    <>
      {effective && (
        <p className="legal-document-effective">
          Version {data.version} &middot; Effective {effective}
        </p>
      )}
      <div
        className="legal-document-content"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.contentHtml) }}
      />
    </>
  );
};

export default RenderedLegalDocument;
